/** @format */

// Shared visibility + validation helpers for the Capex Calculator Input Form.
// Mirrors the shape of cf-v2-step-input-form.js's isFieldVisible/validateStepInputs, but keyed
// off the Capex input-form field contract: { fieldId, inputType: "DROPDOWN" | "NUMBER",
// isMandatory, showWhen: { fieldId, valueIn } | null, ... }.

const isBlank = (v) => v === undefined || v === null || v === "";

// A field applies only when answers[showWhen.fieldId] is one of showWhen.valueIn.
export const isCapexFieldVisible = (field, answers) => {
  if (!field?.showWhen || !field.showWhen.fieldId) return true;
  return (field.showWhen.valueIn || []).includes(answers[field.showWhen.fieldId]);
};

// Returns the first validation error message across all currently-visible fields, or null.
export const validateCapexInputs = (fields = [], answers = {}) => {
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    if (!isCapexFieldVisible(field, answers)) continue;
    if (!field.isMandatory) continue;
    const value = answers[field.fieldId];
    if (isBlank(value)) return `Please provide "${field.fieldLabel}"`;
    if (field.inputType === "NUMBER" && Number.isNaN(Number(value))) {
      return `"${field.fieldLabel}" must be a number`;
    }
  }
  return null;
};

// Given the full field config and the current answers, strips out answers for fields that are
// currently hidden by showWhen (so a stale value from a no-longer-visible field never rides
// along to /calculate).
export const stripHiddenAnswers = (fields = [], answers = {}) => {
  const visibleIds = new Set(
    fields.filter((f) => isCapexFieldVisible(f, answers)).map((f) => f.fieldId)
  );
  const result = {};
  Object.keys(answers).forEach((key) => {
    if (visibleIds.has(key) && !isBlank(answers[key])) {
      result[key] = answers[key];
    }
  });
  return result;
};

// The default answers implied by field config alone (DROPDOWN's isDefault option, NUMBER's
// defaultValue) — used to seed a brand-new (never-calculated-before) Input Form.
export const defaultCapexAnswers = (fields = []) => {
  const defaults = {};
  fields.forEach((field) => {
    if (field.inputType === "DROPDOWN") {
      const defaultOption = (field.options || []).find((o) => o.isDefault);
      if (defaultOption) defaults[field.fieldId] = defaultOption.value;
    } else if (field.inputType === "NUMBER" && !isBlank(field.defaultValue)) {
      defaults[field.fieldId] = field.defaultValue;
    }
  });
  return defaults;
};
